import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiPhone, FiMail, FiMapPin, FiClock, FiSend } from 'react-icons/fi';
import emailjs from '@emailjs/browser';
import '../styles/Pages.css';
import EnhancedHeader from '../components/EnhancedHeader';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: ''
  });

  const [isVerified, setIsVerified] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleVerificationChange = (e) => {
    setIsVerified(e.target.checked);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Spam verification check
    if (!isVerified) {
      setError('Please verify that you are not a robot');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      // Send email to both addresses
      const templateParams = {
        to_emails: 'info@activecareresidential.com, Sanayatt1@hotmail.com',
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'Not provided',
        subject: formData.subject,
        message: formData.message,
      };

      // Initialize EmailJS (replace with your Service ID, Template ID, and Public Key)
      emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY');

      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID',
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID',
        templateParams
      );

      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setIsVerified(false);

      setTimeout(() => {
        setSubmitted(false);
      }, 3000);
    } catch (err) {
      setError('Failed to send message. Please try again later.');
      console.error('Email error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <EnhancedHeader
        title="Get in Touch"
        subtitle="We're here to help and answer any questions"
        backgroundImage="/images/residential-care-banner.jpg"
        backgroundGradient="linear-gradient(135deg, rgba(231, 76, 60, 0.7) 0%, rgba(192, 57, 43, 0.7) 100%)"
      />


      {/* Contact Information with Image 1 */}
      <motion.section
        className="contact-info-section"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info-cards">
              {[
                { icon: FiMapPin, title: 'East Ham Office', details: 'East Ham, London' },
                { icon: FiPhone, title: 'Phone', details: '+447737207521' },
                { icon: FiMail, title: 'Email', details: 'sanayatt1@hotmail.com' },
                { icon: FiClock, title: 'Hours', details: 'Monday-Friday: 9AM-5PM, Emergency: 24/7' }
              ].map((info, i) => (
                <motion.div
                  key={i}
                  className="contact-card"
                  variants={itemVariants}
                  whileHover={{ y: -10, boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)' }}
                >
                  <info.icon className="contact-icon" />
                  <h3>{info.title}</h3>
                  <p>{info.details}</p>
                </motion.div>
              ))}
            </div>

            <div className="contact-image">
              {/* IMAGE PLACEMENT 1: Contact/Office */}
              <img src="/images/acr-contact-3.jpeg" alt="Contact Us" />
            </div>
          </div>
        </div>
      </motion.section>

      {/* Contact Form with Image 2 */}
      <motion.section
        className="contact-form-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <div className="form-grid">
            <div className="form-image">
              {/* IMAGE PLACEMENT 2: Message/Communication */}
              <img src="/images/acr-contact-2.jpeg" alt="Send Message" />
            </div>
            {/* Form Section here */}
            <motion.form
              className="contact-form"
              onSubmit={handleSubmit}
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="section-title">Send us a Message</h2>

              {submitted && (
                <motion.div
                  className="success-message"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  Thank you! We'll be in touch within 2 business days.
                </motion.div>
              )}

              {error && (
                <motion.div
                  className="error-message"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  {error}
                </motion.div>
              )}

              <motion.div className="form-group" variants={itemVariants}>
                <label>Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your name"
                />
              </motion.div>

              <motion.div className="form-group" variants={itemVariants}>
                <label>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your@email.com"
                />
              </motion.div>

              <motion.div className="form-group" variants={itemVariants}>
                <label>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Your phone"
                />
              </motion.div>

              <motion.div className="form-group" variants={itemVariants}>
                <label>Subject *</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="How can we help?"
                />
              </motion.div>

              <motion.div className="form-group" variants={itemVariants}>
                <label>Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Your message..."
                  rows="6"
                />
              </motion.div>

              {/* Spam Verification */}
              <motion.div className="form-group verification-group" variants={itemVariants}>
                <label className="verification-label">
                  <input
                    type="checkbox"
                    checked={isVerified}
                    onChange={handleVerificationChange}
                    className="verification-checkbox"
                  />
                  <span>I confirm I'm not a robot and agree to submit this form</span>
                </label>
              </motion.div>

              <motion.button
                type="submit"
                className="btn btn-primary"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={loading}
              >
                <FiSend /> {loading ? 'Sending...' : 'Send Message'}
              </motion.button>
            </motion.form>

            
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default Contact;